import { Alert, Button, Drawer, Empty, List, Space, Spin, Typography } from 'antd';
import { mergeHistory } from './changeLogClient';
import type { ChangeLogSubject, HistoryItem } from './changeLogTypes';
import { ChangeLine } from './ChangeLine';
import { useChangeLog } from './useChangeLog';
import { actorLabel } from './formatChange';

const Entry: React.FC<{ item: HistoryItem }> = ({ item }) => (
  <List.Item>
    <Space direction="vertical" size={6} style={{ width: '100%' }}>
      <Space size={8} wrap>
        <Typography.Text type="secondary">{new Date(item.at).toLocaleString()}</Typography.Text>
        <Typography.Text>{actorLabel(item)}</Typography.Text>
      </Space>
      <Typography.Text strong>{item.summary}</Typography.Text>
      {item.changes.map((change, i) => (
        <ChangeLine key={`${change.field}-${i}`} change={change} />
      ))}
    </Space>
  </List.Item>
);

/** The list itself; mounted only while the drawer is open, so it reads afresh on every open. */
const ChangeLogBody: React.FC<{ subject: ChangeLogSubject }> = ({ subject }) => {
  const query = useChangeLog(subject);
  if (query.isLoading) {
    return <Spin style={{ display: 'flex', justifyContent: 'center', marginTop: 48 }} />;
  }
  // A failed "Load more" keeps what is already shown (react-query keeps the pages).
  if (!query.data) {
    return (
      <Alert
        type="error"
        showIcon
        message="The change log could not be loaded."
        action={
          <Button size="small" onClick={() => query.refetch()}>
            Retry
          </Button>
        }
      />
    );
  }
  const steps = query.data.pages;
  const items = mergeHistory(steps);
  const failures = [...new Set(steps.flatMap((s) => s.failures))];
  return (
    <Space direction="vertical" size={12} style={{ width: '100%' }}>
      {failures.map((failure) => (
        <Alert key={failure} type="warning" showIcon message={failure} />
      ))}
      {items.length === 0 ? (
        <Empty
          description={failures.length ? 'No other changes recorded.' : 'No changes recorded yet.'}
        />
      ) : (
        <List
          dataSource={items}
          rowKey={(i) => `${i.source}:${i.id}`}
          renderItem={(i) => <Entry item={i} />}
        />
      )}
      {query.isError && <Alert type="error" showIcon message="More changes could not be loaded." />}
      {query.hasNextPage && (
        <Button block loading={query.isFetchingNextPage} onClick={() => query.fetchNextPage()}>
          Load more
        </Button>
      )}
    </Space>
  );
};

export const ChangeLogDrawer: React.FC<{
  open: boolean;
  onClose: () => void;
  subject: ChangeLogSubject;
}> = ({ open, onClose, subject }) => (
  <Drawer
    title="Change log"
    placement="right"
    width={640}
    open={open}
    onClose={onClose}
    destroyOnClose
  >
    {open && <ChangeLogBody subject={subject} />}
  </Drawer>
);
